import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React, { FCC } from 'react';
import styled from 'styled-components';
import { HL_ICON_PREFIX } from '../IconPackHL/iconPackHL';
import './icon.scss';
import { IIconProps } from './types';

const IconWrapper = styled.span`
  --transition-speed: 0.2s;
  &.disabled {
    opacity: 0.5;

    &[role='button']:before {
      display: none;
    }
  }

  &[role='button'] {
    cursor: pointer;
    display: inline-flex;
    height: 1.1em;
    width: 1.1em;
    align-items: center;
    justify-content: center;
    position: relative;
    transition: all var(--transition-speed);

    &:before {
      background-color: ${({ theme }) => theme.iconBackground || '#c3c4c7'};
      border-radius: 50%;
      content: '';
      display: block;
      margin-left: ${({ theme }) => theme.iconBackgroundOffsetLeft || '0'};
      margin-top: ${({ theme }) => theme.iconBackgroundOffsetTop || '0'};
      position: absolute;
      height: 0;
      width: 0;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      opacity: 0;
      transition: all var(--transition-speed);
    }

    &:hover {
      opacity: 1;

      &:before {
        padding: 80%;
        opacity: 0.8;
      }

      svg {
        color: ${({ theme }) => theme.iconHoverColor || '#6f727c'};
      }
    }

    svg {
      pointer-events: none;
      position: relative;
      transition: all var(--transition-speed);
    }
  }
`;

function isValidPrefix(prefix: string) {
  return prefix === 'fas' || prefix === 'far' || prefix === 'fab' || prefix === 'fal';
}

const IconInner: FCC<Omit<IIconProps, 'onClick'>> = (iconProps) => {
  if (
    typeof iconProps.icon !== 'string' &&
    'prefix' in iconProps.icon &&
    iconProps.icon.prefix === HL_ICON_PREFIX &&
    'svg' in iconProps.icon
  ) {
    return (
      // I'm pretty sure that none of the stuff in iconProps is relevant here, but whatever. we will refactor this soon.
      <span {...(iconProps as any)} className={`hl-icon ${iconProps.className}`}>
        {iconProps.icon.svg}
      </span>
    );
  } else {
    return <FontAwesomeIcon {...iconProps} />;
  }
};

export const Icon: FCC<IIconProps> = (props) => {
  const handler = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
    if (!props.onClick || props.disabled) {
      return;
    }
    if (event.type === 'click') {
      props.onClick(event);
      return;
    }
    const keyEvent = event as React.KeyboardEvent<HTMLElement>;
    if (keyEvent.keyCode === 13 || keyEvent.keyCode === 32) {
      props.onClick(keyEvent);
      keyEvent.preventDefault();
    }
  };
  const clickable = !!props.onClick;
  const clickableProps = clickable ? { tabIndex: 0, role: 'button', onClick: handler, onKeyDown: handler } : {};

  let icon = props.icon;
  if (typeof props.icon === 'string') {
    const pieces = props.icon.split(' ');
    if (isValidPrefix(pieces[0])) {
      const name = pieces[1].split('fa-');
      icon = [pieces[0], name[1]] as any;
    }
  }

  const iconClassNames = classNames(props.className, {
    disabled: props.disabled,
    [`fa-${props.size}`]: true,
  });

  const iconProps = Object.keys(props)
    .filter((key) => !['title', 'size', 'className'].includes(key))
    .reduce((obj, key) => {
      obj[key] = props[key];
      return obj;
    }, {});

  return clickable ? (
    <IconWrapper title={props.title} {...clickableProps} className={iconClassNames}>
      <IconInner {...iconProps} icon={icon} />
    </IconWrapper>
  ) : (
    <IconInner title={props.title} className={iconClassNames} {...iconProps} icon={icon} />
  );
};

export default Icon;
